import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../shared/Icon';
import './VoiceRecorderWidget.css';
import { VoiceRecordingModel, MAX_DURATION_MS } from '../../../models/VoiceRecordingModel';
import { useVoiceRecordingViewModel } from '../../../viewModels/useVoiceRecordingViewModel';

function formatClock(ms) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Live input-level waveform. Renders the rolling level history as vertical bars mirrored
 * around the centre line; newest bars enter from the right and older ones scroll off the
 * left. Dims (via `paused`) when capture is paused.
 */
function Waveform({ history, paused }) {
  return (
    <div className={`voice-widget-wave ${paused ? 'paused' : ''}`} aria-hidden="true">
      {history.map((lv, i) => {
        const h = Math.max(8, Math.min(1, lv) * 100);
        return <span key={i} className="voice-widget-wave-bar" style={{ height: `${h}%` }} />;
      })}
    </div>
  );
}

/**
 * In-chat microphone recording widget (PRD-941). Renders inline at the top of the dataset
 * selection panel — not as a modal overlay. Owns its own recording model/VM. The parent
 * mounts it when the user picks "Record from microphone" and unmounts on close/submit.
 */
function VoiceRecorderWidget({ draft, onClose, onShowNotification, onSubmitted }) {
  // One model per mounted widget instance.
  const model = useMemo(() => new VoiceRecordingModel(), []);
  const vm = useVoiceRecordingViewModel(model);
  const { start, restoreDraft } = vm;
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // On mount: recover an interrupted recording as a draft if one exists; otherwise wait
  // in the idle state for the user to press Start (no auto-recording).
  useEffect(() => {
    if (vm.phase !== 'idle') return;
    if (draft) {
      model.loadDraft(draft);
      return;
    }
    restoreDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCapturing = vm.phase === 'recording' || vm.phase === 'paused';
  const remainingMs = Math.max(0, MAX_DURATION_MS - vm.elapsedMs);
  const showWarning = vm.warned && isCapturing;
  const busy = vm.phase === 'submitting' || vm.phase === 'finalizing';
  // The top-corner close only appears before/around recording (idle / permission states),
  // never on the record or submit screens — those are driven by their own controls.
  const showClose = ['idle', 'requesting', 'denied', 'error'].includes(vm.phase);

  const handleClose = () => {
    if (busy) return;
    vm.reset();
    onClose();
  };

  const handleSubmit = async () => {
    const id = await vm.submit();
    if (id) {
      if (onShowNotification) onShowNotification('Recording submitted — processing has started.', 'success');
      if (onSubmitted) onSubmitted(id);
      vm.reset();
      onClose();
    } else if (vm.error && onShowNotification) {
      onShowNotification(vm.error, 'error');
    }
  };

  const handleCancel = async () => {
    await vm.cancel();
    onClose();
  };

  return (
    <div className="voice-widget" role="region" aria-label="Record from microphone">
      <div className="voice-widget-head">
        <span className="voice-widget-overline">Record a dataset</span>
        {vm.phase === 'recording' && (
          <span className="voice-widget-pill voice-widget-pill--rec">
            <span className="voice-widget-pill-dot" /> Recording
          </span>
        )}
        {vm.phase === 'paused' && (
          <span className="voice-widget-pill voice-widget-pill--paused">Paused</span>
        )}
        {showClose && (
          <button
            type="button"
            className="voice-widget-close"
            onClick={handleClose}
            aria-label="Close recorder"
            title="Close"
          >
            <Icon name="close" />
          </button>
        )}
      </div>

      <div className="voice-widget-body">
        {vm.phase === 'idle' && (
          <div className="voice-widget-ready">
            <p className="voice-widget-status">
              Record up to 10 minutes. Click Start when you're set — you can pause or cancel any time.
            </p>
            <button type="button" className="voice-widget-btn-primary" onClick={start}>
              <Icon name="mic" /> Start recording
            </button>
          </div>
        )}

        {vm.phase === 'requesting' && (
          <p className="voice-widget-status">Requesting microphone access…</p>
        )}

        {vm.phase === 'denied' && (
          <div className="voice-widget-notice">
            <Icon name="mic_off" className="voice-widget-notice-icon" size={28} />
            <p className="voice-widget-status">
              Microphone access is blocked. Allow access for this site in your browser's
              address-bar permissions, then try again.
            </p>
            <button type="button" className="voice-widget-btn-secondary" onClick={vm.start}>
              Try again
            </button>
          </div>
        )}

        {vm.phase === 'error' && (
          <div className="voice-widget-notice">
            <Icon name="error_outline" className="voice-widget-notice-icon" size={28} />
            <p className="voice-widget-status">{vm.error || 'Something went wrong.'}</p>
          </div>
        )}

        {isCapturing && (
          <>
            <div className="voice-widget-meter-row">
              <span className="voice-widget-time">
                {formatClock(vm.elapsedMs)}<span className="voice-widget-cap"> / {formatClock(MAX_DURATION_MS)}</span>
              </span>
            </div>
            <div className="voice-widget-progress" aria-hidden="true">
              <div
                className={`voice-widget-progress-fill ${vm.warned ? 'warn' : ''}`}
                style={{ width: `${Math.min(100, (vm.elapsedMs / MAX_DURATION_MS) * 100)}%` }}
              />
            </div>
            <Waveform history={vm.levelHistory} paused={vm.phase === 'paused'} />
            {showWarning && (
              <p className="voice-widget-warning">
                Approaching the 10-minute limit — recording stops automatically in {formatClock(remainingMs)}.
              </p>
            )}
            {confirmingDiscard ? (
              <div className="voice-widget-confirm">
                <p className="voice-widget-status">Discard this recording? This can't be undone.</p>
                <div className="voice-widget-controls">
                  <button type="button" className="voice-widget-btn-secondary" onClick={() => setConfirmingDiscard(false)}>
                    Keep recording
                  </button>
                  <button
                    type="button"
                    className="voice-widget-btn-danger"
                    onClick={async () => { setConfirmingDiscard(false); await handleCancel(); }}
                  >
                    Discard
                  </button>
                </div>
              </div>
            ) : (
              <div className="voice-widget-controls">
                {vm.phase === 'recording' ? (
                  /* While recording, the only control is Pause. */
                  <button type="button" className="voice-widget-btn-icon" onClick={vm.pause} title="Pause" aria-label="Pause">
                    <Icon name="pause" />
                  </button>
                ) : (
                  /* While paused: resume, discard (✕, confirmed), or save (→ name & submit). */
                  <>
                    <button type="button" className="voice-widget-btn-icon" onClick={vm.resume} title="Resume" aria-label="Resume">
                      <Icon name="play_arrow" />
                    </button>
                    <button type="button" className="voice-widget-btn-icon-ghost" onClick={() => setConfirmingDiscard(true)} title="Discard" aria-label="Discard">
                      <Icon name="close" />
                    </button>
                    <button type="button" className="voice-widget-btn-icon-primary" onClick={vm.save} title="Save" aria-label="Save">
                      <Icon name="save" />
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {(vm.phase === 'review' || vm.phase === 'submitting') && (
          <>
            {vm.isDraftRestored && (
              <p className="voice-widget-draft-note">
                <Icon name="restore" />
                Recovered an unfinished recording. Name it and submit, or discard.
              </p>
            )}
            <p className="voice-widget-status">
              Recorded {formatClock(vm.elapsedMs)}. Name this dataset and submit.
            </p>
            <label className="voice-widget-overline" htmlFor="voice-widget-name">Dataset name</label>
            <input
              id="voice-widget-name"
              type="text"
              className="resui-input"
              value={vm.name}
              onChange={(e) => vm.setName(e.target.value)}
              placeholder="Voice note"
              disabled={vm.phase === 'submitting'}
            />
            <div className="voice-widget-controls voice-widget-controls--stack">
              <button
                type="button"
                className="voice-widget-btn-primary"
                onClick={handleSubmit}
                disabled={vm.phase === 'submitting' || !vm.name.trim()}
              >
                {vm.phase === 'submitting' ? 'Submitting…' : 'Submit recording'}
              </button>
              <button
                type="button"
                className="voice-widget-btn-secondary"
                onClick={vm.back}
                disabled={vm.phase === 'submitting'}
              >
                <Icon name="arrow_back" /> Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VoiceRecorderWidget;
